/**
 * Écran 2 — Formulaire. Identité, e-mail, fuseau détecté+confirmable, grille
 * de disponibilités, puis deux choix simples : « quand peux-tu commencer » et
 * « ta situation ». Un seul POST /profile au clic sur « Continuer » (gèle le
 * tirage des cartes côté serveur, cf. contrat).
 *
 * Choix produit (feedback Kayla 14-07) : on ne demande PAS le nombre d'heures
 * (c'est Kayla qui le fixe) ni une date de début à saisir (« de la merde ») —
 * on veut juste savoir s'il peut commencer maintenant et sa situation. Les
 * contacts Telegram/WhatsApp sont retirés (l'e-mail suffit).
 */
import { useState, type ReactNode } from "react";

import { Alerte, Button, Carte, Champ, Etiquette, Saisie, Temps } from "@/shared/ui";
import { Globe2 } from "@/shared/ui/icons";
import { deviceTimezone, formatOffsetLabel } from "@/shared/timezone";

import {
  START_AVAILABILITY_OPTIONS,
  WORK_SITUATION_OPTIONS,
  GENDER_OPTIONS,
  AGE_MINIMUM,
  type ChoiceOption,
} from "../constants";
import type {
  Availability,
  Gender,
  ProfileFormValues,
  StartAvailability,
  WorkSituation,
} from "../types";

import { ageRevolu, naissanceLaPlusTardive } from "./age";

import { ShiftAvailabilityPicker } from "./ShiftAvailabilityPicker";

interface ProfileFormScreenProps {
  onSubmit: (values: ProfileFormValues) => void;
  submitting: boolean;
  errorMessage?: string | null;
}

/** Une question sans champ (genre, fuseau, créneaux…) : l'étiquette de la librairie, et sa précision en gris dessous. */
function Question({ titre, precision, children }: { titre: string; precision?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <Etiquette as="div">{titre}</Etiquette>
        {precision && <p className="text-[13px] leading-snug text-muted">{precision}</p>}
      </div>
      {children}
    </div>
  );
}

/** Groupe de choix « gros boutons » — une seule sélection : une puce par ligne, pleine largeur, son emoji devant ;
 *  la choisie prend la marque (`aria-pressed`, c'est le `Button` de la librairie qui le pose). */
function ChoiceGroup<T extends string>({
  options,
  value,
  onChange,
  disabled,
  nom,
}: {
  options: readonly ChoiceOption<T>[];
  value: T | null;
  onChange: (v: T) => void;
  disabled?: boolean;
  nom: string;
}) {
  return (
    <div role="group" aria-label={nom} className="flex flex-col gap-2">
      {options.map((opt) => (
        <Saisie.Puce
          key={opt.slug}
          taille="lg"
          actif={value === opt.slug}
          disabled={disabled}
          icone={<span aria-hidden>{opt.emoji}</span>}
          onClick={() => onChange(opt.slug)}
          className="min-h-12 w-full justify-start px-4 text-[15px]"
        >
          {opt.label}
        </Saisie.Puce>
      ))}
    </div>
  );
}

export function ProfileFormScreen({
  onSubmit,
  submitting,
  errorMessage,
}: ProfileFormScreenProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState<Gender | null>(null);
  const [birthDate, setBirthDate] = useState("");
  const [email, setEmail] = useState("");
  const [discord, setDiscord] = useState("");
  const [timezone, setTimezone] = useState(() => deviceTimezone());
  const [showTzPicker, setShowTzPicker] = useState(false);
  const [availability, setAvailability] = useState<Availability>({});
  const [startAvailability, setStartAvailability] = useState<StartAvailability | null>(null);
  const [workSituation, setWorkSituation] = useState<WorkSituation | null>(null);
  const [touched, setTouched] = useState(false);

  const age = ageRevolu(birthDate);

  const validationError = !firstName.trim() || !lastName.trim()
    ? "Prénom et nom sont obligatoires."
    : !gender
      ? "Dis-nous si tu es un homme ou une femme."
      : age === null
        ? "Ta date de naissance est obligatoire."
        : age < AGE_MINIMUM
          ? `Il faut avoir ${AGE_MINIMUM} ans pour ce poste.`
          : !email.trim()
      ? "L'e-mail est obligatoire."
      : !startAvailability
        ? "Dis-nous quand tu peux commencer."
        : !workSituation
          ? "Dis-nous ta situation."
          : null;

  /** `Saisie.Formulaire` a déjà empêché le rechargement de la page. */
  function handleSubmit() {
    setTouched(true);
    if (validationError || submitting || !startAvailability || !workSituation) return;
    if (!gender) return;
    onSubmit({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      gender,
      birth_date: birthDate,
      email: email.trim(),
      contact_discord: discord.trim() || null,
      timezone,
      availability,
      start_availability: startAvailability,
      work_situation: workSituation,
    });
  }

  /** Le pied : l'erreur ET « Continuer » — collés en bas de l'écran sur un téléphone (`ActionsDeFormulaire`), donc
   *  le bouton se voit sans descendre tout le formulaire, et le refus s'affiche là où le doigt vient d'appuyer. */
  const pied = (
    <div className="flex w-full flex-col gap-3">
      {touched && (validationError || errorMessage) && (
        <Alerte ton="erreur" taille="sm" texte={validationError || errorMessage} />
      )}
      <Button type="submit" size="xl" variant="accent" disabled={submitting} className="w-full justify-center">
        {submitting ? "Enregistrement…" : "Continuer →"}
      </Button>
    </div>
  );

  return (
    // `noValidate={false}` : le navigateur garde la main sur le format de l'e-mail (la garde d'âge et les champs
    // obligatoires restent dans `validationError`, comme avant).
    <Saisie.Formulaire onEnvoyer={handleSubmit} noValidate={false} actions={pied} className="gap-5">
      <div className="flex flex-col gap-2">
        <Etiquette as="div">Étape 1</Etiquette>
        <h2 className="text-[26px] font-extrabold leading-tight tracking-tight text-fg">
          Faisons connaissance
        </h2>
      </div>

      {/* ─── L'introduction ──────────────────────────────────────────────
          Kayla, 18-08 : « j'aimerais un petit truc d'introduction : qu'ils
          aient du contexte, qu'ils sachent que là c'est un petit entraînement
          en presque conditions réelles. »

          Elle vit ICI et pas sur une page à elle : l'écran « Bienvenue » a été
          supprimé le même jour (« ça supprime cette page-là »), et le parcours
          doit rester en trois temps — vérifs, formulaire, épreuve. Le
          formulaire étant devenu le premier écran, c'est lui qui porte le
          contexte.

          🩸 13-09-2026, plus tard : « Une IA gère 80 % du travail » est parti
          aussi — Kayla : « c'est plus vraiment vrai ». L'IA fait le relationnel
          et la chauffe ; le chatteur ÉCRIT, négocie et vend. Et la paie est en
          USDT, pour des gens EXPÉRIMENTÉS.

          🩸 13-09-2026 : elle ne parle plus d'entraînement ni de test. Le
          parcours public s'arrête au formulaire depuis le 19-08, et Kayla ne
          donne le test QU'à la création du compte (« je ne le mettrai que
          quand ils créent leur compte »). Promettre ici un test qui ne vient
          pas faisait chercher au candidat une épreuve qui n'existe plus sur
          ce chemin.

          🔞 L'avertissement contenu adulte y est FONDU plutôt que posé à côté.
          Séparés, ils disaient deux fois la même chose ; ensemble, ils
          racontent le poste dans l'ordre où on se le demande : c'est quoi, avec
          qui, ce que je fais exactement, ce que ça rapporte.

          🩸 07-10-2026 : ramassée en trois lignes, sans émojis (Kayla : « le
          plus minimaliste et le plus simple possible »). Les mêmes faits,
          dans le même ordre ; un test les tient.

          Une `Carte` de verre : plus de contour gris ni de trait entre les
          deux paragraphes — l'avertissement se détache par son blanc. */}
      <Carte remplissage="lg" className="flex flex-col gap-3.5 text-[14.5px] leading-relaxed text-muted">
        <p>
          Tu vas travailler pour des <b className="text-fg">créatrices de
          contenu à caractère sexuel</b>. Une IA chauffe les fans, puis{" "}
          <b className="text-fg">c'est toi qui prends la main</b> : tu écris,
          tu négocies, tu vends.
        </p>
        <p>
          <b className="text-fg">10 % de chaque vente</b>,{" "}
          <b className="text-fg">en USDT</b>. Réservé aux{" "}
          <b className="text-fg">chatteurs expérimentés</b>.
        </p>
        <p className="mt-1 text-fg">
          <b>Contenu adulte.</b>{" "}
          <span>
            Tu écris des choses très crues à des inconnus. Si ça te met mal à
            l'aise, cette annonce n'est pas pour toi.
          </span>
        </p>
      </Carte>

      {/* Deux colonnes quand la colonne du formulaire a la place (≥ 448 px), une sur un téléphone. */}
      <Saisie.SectionDeFormulaire colonnes={2}>
        <Champ
          etiquette="Prénom"
          taille="lg"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Camille"
          autoComplete="given-name"
          disabled={submitting}
        />
        <Champ
          etiquette="Nom"
          taille="lg"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Durand"
          autoComplete="family-name"
          disabled={submitting}
        />
      </Saisie.SectionDeFormulaire>

      {/* Genre — deux choix, comme Kayla l'a formulé le 18-08 : « si c'est un
          homme ou une femme ». */}
      <Question titre="Tu es…">
        <ChoiceGroup nom="Tu es…" options={GENDER_OPTIONS} value={gender} onChange={setGender} disabled={submitting} />
      </Question>

      {/* 📅 La DATE, jamais l'âge — un âge saisi est faux un an plus tard, et
          personne ne revient le corriger. `max` borne le sélecteur natif au
          jour des 18 ans : le candidat ne peut même pas choisir une date qui
          le refuserait. La garde reste dans `validationError` ET côté serveur,
          parce qu'un attribut HTML ne verrouille rien.

          ⛔ `Temps.ChampDate` ne le remplace PAS (encore) : son calendrier ne
          sait avancer que d'un mois à la fois, sans choix d'année — une
          naissance de 1998 serait à 330 clics. Le sélecteur natif d'un
          téléphone, lui, a sa roue des années. */}
      <Champ
        etiquette="Date de naissance"
        taille="lg"
        type="date"
        value={birthDate}
        onChange={(e) => setBirthDate(e.target.value)}
        max={naissanceLaPlusTardive(AGE_MINIMUM)}
        disabled={submitting}
        aide={age !== null && age >= AGE_MINIMUM ? `${age} ans` : undefined}
      />

      <Champ
        etiquette="E-mail"
        taille="lg"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="camille@email.com"
        autoComplete="email"
        disabled={submitting}
      />

      {/* Kayla, 18-08 : elle veut le Discord dans la table des leads. C'est
          par là qu'on relance un candidat — le recrutement vit sur le serveur
          Cerbère. Le CHAMP reste optionnel, et ça compte : le rendre
          obligatoire ferait perdre des candidatures pour une commodité de
          contact, alors que l'e-mail juste au-dessus suffit à joindre n'importe
          qui — le pseudo saisi ici ne sert à rien techniquement.

          Le 06-10-2026 Kayla a voulu que l'obligation soit claire dès le
          début. Elle l'est dans le TEXTE, pas dans le champ : le vrai lien se
          fait par OAuth à la porte du cockpit (cf `MemberProfileCompletion`),
          et c'est lui qui fait entrer sur le serveur. « Si tu en as un »
          laissait croire qu'on pouvait s'en passer.

          Le mot « Discord » est l'étiquette du champ, le reste est son aide :
          une étiquette en capitales ne porte pas une phrase. */}
      <Champ
        etiquette="Discord"
        aide="Obligatoire pour travailler avec nous, tu le connecteras à l'étape suivante"
        taille="lg"
        type="text"
        value={discord}
        onChange={(e) => setDiscord(e.target.value)}
        placeholder="camille_d"
        autoComplete="off"
        disabled={submitting}
      />

      {/* Le fuseau : détecté d'abord (il se confirme d'un coup d'œil), et « Changer » ouvre le sélecteur de la
          librairie — l'appareil en tête (« Détecté »), les marchés par région, « Autre » si la valeur n'y est pas. */}
      <Question titre="Fuseau horaire">
        {showTzPicker ? (
          <Temps.SelecteurFuseau
            taille="lg"
            aria-label="Fuseau horaire"
            value={timezone}
            onChange={setTimezone}
            disabled={submitting}
          />
        ) : (
          <Carte remplissage="sm" className="flex flex-col gap-3 @[480px]/panel:flex-row @[480px]/panel:items-center">
            <div className="flex min-w-0 flex-1 items-start gap-3 text-[14px] leading-snug text-muted">
              <Globe2 aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
              <span className="min-w-0 break-words">
                Détecté : <b className="text-fg">{timezone}</b> ({formatOffsetLabel(timezone)})
              </span>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="min-h-11 w-full shrink-0 px-5 text-[14px] @[480px]/panel:w-auto"
              disabled={submitting}
              onClick={() => setShowTzPicker(true)}
            >
              Changer
            </Button>
          </Carte>
        )}
      </Question>

      <Question titre="Tes disponibilités" precision="Tape les shifts que tu peux faire">
        <ShiftAvailabilityPicker
          value={availability}
          onChange={setAvailability}
          timezone={timezone}
        />
      </Question>

      <Question titre="Tu peux commencer quand ?">
        <ChoiceGroup
          nom="Tu peux commencer quand ?"
          options={START_AVAILABILITY_OPTIONS}
          value={startAvailability}
          onChange={setStartAvailability}
          disabled={submitting}
        />
      </Question>

      <Question titre="Ta situation en ce moment">
        <ChoiceGroup
          nom="Ta situation en ce moment"
          options={WORK_SITUATION_OPTIONS}
          value={workSituation}
          onChange={setWorkSituation}
          disabled={submitting}
        />
      </Question>

    </Saisie.Formulaire>
  );
}
